import { useEffect, useMemo, useState } from 'react';
import { phase3API } from '../services/api';
import { SessionData } from '../types';
import { getPatentDisplayCountInfo, parseQualityDistribution } from '../utils/patentDisplayCount';
import '../css/AdminSessionPreview.css';

type PreviewState =
  | { status: 'idle'; session: null; error: '' }
  | { status: 'loading'; session: null; error: '' }
  | { status: 'loaded'; session: SessionData; error: '' }
  | { status: 'error'; session: null; error: string };

const getInitialSessionId = () => {
  const params = new URLSearchParams(window.location.search);
  return (params.get('session_id') || '').trim();
};

const formatCount = (value: number) => value.toLocaleString('ja-JP');

/**
 * Read-only admin page for previewing the session fields that drive customer
 * result-screen counts, without impersonating the customer account.
 */
const AdminSessionPreview = () => {
  const [sessionId, setSessionId] = useState(getInitialSessionId);
  const [state, setState] = useState<PreviewState>({
    status: 'idle',
    session: null,
    error: '',
  });

  const normalizedSessionId = sessionId.trim();

  const loadSession = async () => {
    if (!normalizedSessionId) {
      setState({
        status: 'error',
        session: null,
        error: 'セッションIDを入力してください。',
      });
      return;
    }

    setState({ status: 'loading', session: null, error: '' });
    const params = new URLSearchParams(window.location.search);
    params.set('session_id', normalizedSessionId);
    window.history.replaceState(null, '', `/admin/session-preview?${params.toString()}`);

    try {
      const session = await phase3API.getAdminSessionPreview(normalizedSessionId);
      setState({ status: 'loaded', session, error: '' });
    } catch (error) {
      setState({
        status: 'error',
        session: null,
        error:
          error instanceof Error && error.message
            ? error.message
            : 'セッション情報の取得に失敗しました。',
      });
    }
  };

  useEffect(() => {
    if (normalizedSessionId) {
      void loadSession();
    }
    // Run only on initial mount. Manual reload is handled by the button.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const preview = useMemo(() => {
    if (state.status !== 'loaded') return null;
    const session = state.session;
    const qualityDistribution = parseQualityDistribution(session.quality_distribution);
    const displayInfo = getPatentDisplayCountInfo(session, qualityDistribution);
    return { session, qualityDistribution, displayInfo };
  }, [state]);

  return (
    <main className="admin-preview-page">
      <section className="admin-preview-hero">
        <div>
          <p className="admin-preview-eyebrow">Admin read-only preview</p>
          <h1>セッション表示プレビュー</h1>
          <p>
            顧客アカウントへ切り替えずに、対象セッションの表示件数と参照元を確認します。
            操作系のボタンは持たない、確認専用画面です。
          </p>
        </div>
        <a href="/" className="admin-preview-home">
          管理画面へ戻る
        </a>
      </section>

      <section className="admin-preview-search">
        <label htmlFor="admin-preview-session-id">セッションID</label>
        <div className="admin-preview-search-row">
          <input
            id="admin-preview-session-id"
            value={sessionId}
            onChange={(event) => setSessionId(event.target.value)}
            placeholder="SESSION-YYYYMMDD-..."
          />
          <button type="button" onClick={loadSession} disabled={state.status === 'loading'}>
            {state.status === 'loading' ? '読み込み中' : 'プレビュー'}
          </button>
        </div>
      </section>

      {state.status === 'error' && <div className="admin-preview-error">{state.error}</div>}

      {preview && (
        <section className="admin-preview-content">
          <div className="admin-preview-banner">
            <strong>管理者プレビュー</strong>
            <span>実データを read-only で表示しています。</span>
          </div>

          <div className="admin-preview-summary-grid">
            <div className="admin-preview-card admin-preview-card-primary">
              <span className="admin-preview-card-label">画面表示上の特許数</span>
              <strong>{formatCount(preview.displayInfo.count)}</strong>
              <span className="admin-preview-source">source: {preview.displayInfo.source}</span>
            </div>
            <div className="admin-preview-card">
              <span className="admin-preview-card-label">検索式</span>
              <strong>{formatCount(preview.session.selected_formula_ids?.length || 0)}</strong>
            </div>
            <div className="admin-preview-card">
              <span className="admin-preview-card-label">実現方法</span>
              <strong>{formatCount(preview.session.selected_realization_ids?.length || 0)}</strong>
            </div>
          </div>

          <div className="admin-preview-details">
            <div>
              <h2>セッション状態</h2>
              <dl>
                <div>
                  <dt>session_id</dt>
                  <dd>{preview.session.session_id || normalizedSessionId}</dd>
                </div>
                <div>
                  <dt>user_id</dt>
                  <dd>{preview.session.user_id || '-'}</dd>
                </div>
                <div>
                  <dt>status</dt>
                  <dd>{preview.session.status || '-'}</dd>
                </div>
                <div>
                  <dt>current_step</dt>
                  <dd>{preview.session.current_step}</dd>
                </div>
              </dl>
            </div>

            <div>
              <h2>表示件数の内訳</h2>
              <dl>
                <div>
                  <dt>quality_distribution 合計</dt>
                  <dd>{formatCount(preview.displayInfo.qualityDistributionCount)}</dd>
                </div>
                <div>
                  <dt>loaded_patents_count</dt>
                  <dd>{formatCount(preview.displayInfo.loadedPatentsCount)}</dd>
                </div>
                <div>
                  <dt>final_patents_count</dt>
                  <dd>{formatCount(preview.displayInfo.finalPatentsCount)}</dd>
                </div>
                <div>
                  <dt>final_patents.length</dt>
                  <dd>{formatCount(preview.displayInfo.finalPatentsLength)}</dd>
                </div>
              </dl>
            </div>
          </div>

          <div className="admin-preview-note">
            実際の顧客画面と同じ表示件数 fallback （quality_distribution → loaded_patents_count →
            final_patents_count → final_patents.length）で計算しています。
          </div>
        </section>
      )}
    </main>
  );
};

export default AdminSessionPreview;
